
import {
  BookOpen,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Edit,
  FileText,
  Layers,
  LayoutDashboard,
  LogOut,
  UserCircle
} from 'lucide-react';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { cn } from '../../lib/utils';
//@ts-ignore  
import fullLogo from '../../assets/logo/ByteSplash_Logo.png';
//@ts-ignore  
import logoOnly from '../../assets/logo/Logo_only.png';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { signOut } from 'firebase/auth';
import { useToast } from '../../hooks/use-toast';
import { auth } from '../../lib/firebase';

interface SidebarProps {
  className?: string;
  isLoggedIn?: boolean;
}

const Sidebar = ({ className, isLoggedIn = false }: SidebarProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const { toast } = useToast();
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [activeSubMenuItem, setActiveSubMenuItem] = useState<string | null>(null);
  const sidebarItems: any[] = [
    { icon: ClipboardCheck, label: 'Attendance', path: '/attandance' },
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    // { icon: Clock, label: 'Punch', path: '/punch' },
    { icon: BookOpen, label: 'Lms', path: '/lms' },
    { icon: Calendar, label: 'Schedules', path: '/schedules' },
    { icon: FileText, label: 'Classes', path: '/classes' },
    { icon: Layers, label: 'Batches', path: '/batches' },
    { icon: BookOpen, label: 'Subjects', path: '/subjects' },
    { icon: Edit, label: 'Manipulate', path: '/manipulation' },
    { icon: UserCircle, label: 'Profile', path: '/profile' },
  ];


  const handleSubMenuClick = (subItemPath: string) => {
    setActiveSubMenuItem(subItemPath);
  };

  const toggleSidebar = () => {
    // When collapsing the sidebar, reset the open submenu  
    setCollapsed((prev) => {
      // Collapse state changes, reset the open submenu  
      if (prev) {
        setOpenSubMenu(null); // Collapse all submenus  
      }
      return !prev; // Toggle the sidebar collapsed state  
    });
    setActiveSubMenuItem(null);
    setOpenSubMenu(null);
  };

   const handleLogout = async () => {
      await signOut(auth);
      localStorage.clear();
      window.location.href = "/login";
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account",
      });
      // Add actual logout logic here if necessary  
    };

  return (
    <>
      {isLoggedIn && (
        <div
          className={cn(
            'h-auto min-h-auto bg-bytesplash-blue text-white flex flex-col transition-all duration-300 ease-in-out relative z-20',
            collapsed ? 'w-[4.5rem]' : 'w-[15rem]',
            className
          )}
        >
          <div className={cn(
            'flex bg-white items-center p-4 border-b border-white/10',
            collapsed ? 'justify-center' : 'justify-start'
          )}>
            <div className="flex items-center bg-white min-h-full">
              {collapsed ? (
                <NavLink to="" className="mr-1 flex items-center bg-white">
                  <div className="w-8 h-8 grid place-items-center bg-white rounded">
                    <img src={logoOnly} alt="Bytesplash Logo" width={60} height={60} className="animate-rotateSlow" />
                  </div>
                </NavLink>
              ) : (
                <NavLink to="" className="flex flex-col items-center">
                  <img src={fullLogo} alt="Bytesplash Logo" className="w-36 h-auto" />
                  <span className="text-gray-500 font-semibold text-md" style={{ fontFamily: 'Calibri, sans-serif' }}>Information portal</span>
                </NavLink>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col mt-5 overflow-y-auto min-h-auto">
            <TooltipProvider delayDuration={0}>
              <ul className="space-y-1 px-3">
                {sidebarItems.map((item, index) => (
                  <li key={index}>
                    {item?.hasSubmenu ? (
                      <div>
                        <Collapsible
                          onOpenChange={(open) => {
                            if (!collapsed) {
                              setOpenSubMenu(open ? item.label : null);
                            }
                          }}
                          open={openSubMenu === item.label}
                          className="space-y-1"
                        >
                          <CollapsibleTrigger className="w-full">
                            <div
                              className={cn(
                                'flex items-center justify-between w-full rounded-md p-3 transition-colors',
                                openSubMenu === item.label
                                  ? 'bg-white text-bytesplash-blue'
                                  : 'text-white'
                              )}
                            >
                              <div className="flex items-center space-x-3">
                                {collapsed ? (
                                  <item.icon size={20} />
                                ) : (
                                  <>
                                    <item.icon size={20} />
                                    <span>{item.label}</span>
                                  </>
                                )}
                              </div>
                              <ChevronDown
                                size={16}
                                className={cn(
                                  'transition-transform duration-200',
                                  openSubMenu === item.label ? 'rotate-180' : ''
                                )}
                              />
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="space-y-1 ml-8">
                            {item.submenuItems?.map((subItem, subIndex) => (
                              <NavLink
                                key={subIndex}
                                to={subItem.path}
                                onClick={() => handleSubMenuClick(subItem.path)}
                                className={({ isActive }) => cn(
                                  'flex items-center space-x-3 rounded-md p-2 transition-colors',
                                  (isActive && activeSubMenuItem === subItem.path) // Highlight only the active submenu  
                                    ? 'bg-white text-bytesplash-blue'
                                    : 'hover:bg-bytesplash-blue/80 hover:text-white/90 text-white/70'
                                )}
                              >
                                <subItem.icon size={16} />
                                <span className="text-sm">{subItem.label}</span>
                              </NavLink>
                            ))}
                          </CollapsibleContent>
                        </Collapsible>
                      </div>
                    ) : (
                      <NavLink
                        to={item.path}
                        onClick={() => {
                          setOpenSubMenu(null);
                          setActiveSubMenuItem(null); // Reset active submenu when main link is clicked  
                        }}
                        className={({ isActive }) => cn(
                          'flex items-center space-x-3 rounded-md p-3 transition-colors',
                          isActive
                            ? 'bg-white text-bytesplash-blue'
                            : 'hover:bg-bytesplash-blue hover:text-white/90 text-white/70'
                        )}
                      >
                        {collapsed ? (
                          <item.icon size={20} />
                        ) : (
                          <>
                            <item.icon size={20} />
                            <span>{item.label}</span>
                          </>
                        )}
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            </TooltipProvider>
          </div>

          <div className={cn(
            'p-4 border-t border-white/10 flex',
            collapsed ? 'justify-center' : 'justify-start'
          )}>
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-white/70 hover:text-white/90 hover:bg-bytesplash-blue">
                    <LogOut size={20} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Logout</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button onClick={handleLogout} variant="ghost" className="w-full text-white/70 hover:text-white/90 hover:bg-bytesplash-blue flex justify-start gap-3">
                <LogOut size={20} />
                <span>Logout</span>
              </Button>
            )}
          </div>

          <button
            onClick={toggleSidebar}
            className="absolute -right-3 top-20 bg-white text-bytesplash-blue rounded-full p-1 shadow-md hover:bg-gray-100 transition-colors"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>)}
    </>
  );
};

export default Sidebar;
